import type {
	EditorState as CodeMirrorEditorState,
	Transaction as CodeMirrorTransaction,
} from "@codemirror/state";
import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import type {
	EditorState as ProseMirrorEditorState,
	Transaction as ProseMirrorTransaction,
} from "prosemirror-state";
import { useCallback } from "react";

import {
	$applyPageTransaction,
	$compilePage,
	$pageEditorState,
	$pageMarkdownEditorState,
} from "../../internal/page/page-editor-state.atom";

export type CreateTransactionFn = (
	state: ProseMirrorEditorState | CodeMirrorEditorState
) => ProseMirrorTransaction | CodeMirrorTransaction | undefined;

export type EaselTransaction = {
	createTransaction: CreateTransactionFn;
	shouldComputeImmediately?: boolean;
};

/**
 * Hook for managing page editor state
 * Provides the editor state and a function to apply transactions
 *
 * This hook matches the interface of the XState-based usePageEditorState
 */
export const usePageEditorState = (documentId: string, pageId: string) => {
	const $loadablePageEditorState = loadable($pageEditorState({ documentId, pageId }));
	$loadablePageEditorState.debugLabel = `loadablePageEditorState/${documentId}/${pageId}`;

	const loadablePageEditorState = useAtomValue($loadablePageEditorState);
	const markdownEditorState = useAtomValue($pageMarkdownEditorState({ documentId, pageId }));
	const applyTransactionAtom = useSetAtom($applyPageTransaction({ documentId, pageId }));
	const compilePageAtom = useSetAtom($compilePage({ documentId, pageId }));

	const editorState =
		loadablePageEditorState.state === "hasData" ? loadablePageEditorState.data : undefined;

	const applyTransaction = useCallback(
		({ createTransaction, shouldComputeImmediately }: EaselTransaction) => {
			if (!editorState) {
				console.error("No editor state", { documentId, pageId });
				throw new Error("No editor state");
			}

			const transaction = createTransaction(editorState);
			if (!transaction) {
				console.error("No transaction", { documentId, pageId });
				throw new Error("No transaction");
			}

			// Apply the transaction
			applyTransactionAtom(transaction);

			// If immediate computation is requested, trigger compilation now
			if (shouldComputeImmediately) {
				void compilePageAtom();
			}
		},
		[editorState, applyTransactionAtom, compilePageAtom, documentId, pageId]
	);

	return { editorState, applyTransaction, markdownEditorState };
};
